import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, formatErrorDetail } from "@/lib/api";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AnlagenManagerDialog({ open, onOpenChange, anlagen, onChanged }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const addOne = async () => {
    const v = name.trim();
    if (!v) return;
    setBusy(true);
    try {
      await api.post("/anlagen", { name: v });
      setName("");
      onChanged?.();
      toast.success("Anlage angelegt");
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Fehler");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Anlage wirklich löschen? Sie wird auch aus bestehenden Updates entfernt.")) return;
    try {
      await api.delete(`/anlagen/${id}`);
      onChanged?.();
      toast.success("Anlage entfernt");
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Fehler");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Anlagen verwalten</DialogTitle>
          <DialogDescription>
            Hinzufügen oder Entfernen von Anlagen (z. B. „Sammlung und Beförderung").
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Neue Anlage…"
            className="border-slate-300"
            data-testid="anlagen-manager-input"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addOne();
              }
            }}
          />
          <Button
            type="button"
            onClick={addOne}
            disabled={busy || !name.trim()}
            className="bg-[#E2001A] hover:bg-[#C20015] text-white"
            data-testid="anlagen-manager-add"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </Button>
        </div>

        <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-72 overflow-auto">
          {anlagen.length === 0 ? (
            <div className="p-4 text-sm text-slate-500 text-center">Noch keine Anlagen.</div>
          ) : (
            anlagen.map((a) => (
              <div
                key={a.id}
                className="flex items-center justify-between px-3 py-2"
                data-testid={`anlagen-manager-row-${a.id}`}
              >
                <span className="text-sm text-slate-700">{a.name}</span>
                <button
                  type="button"
                  onClick={() => remove(a.id)}
                  className="text-slate-400 hover:text-red-600"
                  aria-label={`${a.name} löschen`}
                  data-testid={`anlagen-manager-delete-${a.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
